/**
 * Change classifier — Phase 5.
 * Maps DiffItem → Classification using the §5.7 lookup table.
 * Pure function. No side-effects.
 */

import type { DiffItem, DiffItemType } from './diff-engine'

export type Classification =
  | 'safe'
  | 'breaking'
  | 'cosmetic_runtime'
  | 'breaking_pending_data_check' // classification confirmed by Phase 6 impact analysis

export interface ClassifiedChange {
  item: DiffItem
  classification: Classification
  /** Human-readable Persian label for the admin UI */
  label: string
  /** Detail string (e.g. old type → new type) */
  detail?: string
}

// ─── Label builders ──────────────────────────────────────────────────────────

function fieldLabel(item: DiffItem): string {
  const key = item.fieldKey ?? '?'
  const label =
    (item.after as { fa?: string } | undefined)?.fa ??
    (item.before as { fa?: string } | undefined)?.fa ??
    key
  return label
}

const LABELS: Record<DiffItemType, (item: DiffItem) => { label: string; detail?: string }> = {
  field_added: (i) => ({
    label: `فیلد جدید «${fieldLabel(i)}»`,
    detail: i.details?.isRequired ? 'اجباری' : 'اختیاری',
  }),
  field_removed: (i) => ({ label: `فیلد «${fieldLabel(i)}» حذف شد` }),
  field_label_changed: (i) => ({ label: `عنوان فیلد تغییر کرد`, detail: `${i.fieldKey}` }),
  field_description_changed: (i) => ({ label: `توضیح فیلد «${i.fieldKey}» تغییر کرد` }),
  field_required_changed: (i) => ({
    label: i.after ? `فیلد «${i.fieldKey}» اجباری شد` : `فیلد «${i.fieldKey}» اختیاری شد`,
  }),
  field_type_changed: (i) => ({
    label: `نوع فیلد «${i.fieldKey}» تغییر کرد`,
    detail: `${i.before} → ${i.after}`,
  }),
  field_default_changed: (i) => ({ label: `مقدار پیش‌فرض فیلد «${i.fieldKey}» تغییر کرد` }),
  option_added: (i) => ({ label: `گزینه جدید به فیلد «${i.fieldKey}» اضافه شد` }),
  option_removed: (i) => ({
    label: `گزینه از فیلد «${i.fieldKey}» حذف شد`,
    detail: (i.details?.optionLabel as string | undefined) ?? (i.details?.optionKey as string | undefined),
  }),
  option_label_changed: (i) => ({ label: `عنوان گزینه در «${i.fieldKey}» تغییر کرد` }),
  option_order_changed: (i) => ({ label: `ترتیب گزینه‌های «${i.fieldKey}» تغییر کرد` }),
  validation_added: (i) => ({
    label: `اعتبارسنجی به فیلد «${i.fieldKey}» اضافه شد`,
    detail: i.details?.ruleType as string | undefined,
  }),
  validation_removed: (i) => ({ label: `اعتبارسنجی از فیلد «${i.fieldKey}» حذف شد` }),
  validation_tightened: (i) => ({ label: `اعتبارسنجی فیلد «${i.fieldKey}» محدودتر شد` }),
  validation_loosened: (i) => ({ label: `اعتبارسنجی فیلد «${i.fieldKey}» آزادتر شد` }),
  validation_severity_changed: (i) => ({
    label: `شدت اعتبارسنجی «${i.fieldKey}» تغییر کرد`,
    detail: `${i.before} → ${i.after}`,
  }),
  validation_message_changed: (i) => ({ label: `پیام خطای اعتبارسنجی «${i.fieldKey}» تغییر کرد` }),
  conditional_added: (i) => ({
    label: `قانون شرطی جدید: «${(i.details?.description as string | undefined) ?? i.details?.ruleId}»`,
  }),
  conditional_removed: (i) => ({
    label: `قانون شرطی حذف شد: «${(i.details?.description as string | undefined) ?? i.details?.ruleId}»`,
  }),
  conditional_action_changed: (i) => ({
    label: `اقدام قانون شرطی تغییر کرد: «${(i.details?.description as string | undefined) ?? i.details?.ruleId}»`,
  }),
  conditional_predicate_changed: (i) => ({
    label: `شرط قانون شرطی تغییر کرد: «${(i.details?.description as string | undefined) ?? i.details?.ruleId}»`,
  }),
  calculated_added: (i) => ({ label: `فیلد محاسباتی «${i.fieldKey}» اضافه شد` }),
  calculated_removed: (i) => ({ label: `فیلد محاسباتی «${i.fieldKey}» حذف شد` }),
  calculated_formula_changed: (i) => ({ label: `فرمول «${i.fieldKey}» تغییر کرد` }),
  form_title_changed: () => ({ label: 'عنوان فرم تغییر کرد' }),
  form_description_changed: () => ({ label: 'توضیح فرم تغییر کرد' }),
  form_required_for_final_submit_changed: (i) => ({
    label: i.after ? 'فرم برای تکمیل نهایی اجباری شد' : 'فرم دیگر برای تکمیل نهایی اجباری نیست',
  }),
  form_minimum_instances_changed: (i) => ({
    label: 'حداقل تعداد نمونه‌های فرم تغییر کرد',
    detail: `${i.before} → ${i.after}`,
  }),
  form_instance_label_field_changed: () => ({ label: 'فیلد برچسب نمونه تغییر کرد' }),
  form_instance_type_changed: (i) => ({
    label: 'نوع نمونه‌سازی فرم تغییر کرد',
    detail: `${i.before} → ${i.after}`,
  }),
  field_reordered: () => ({ label: 'ترتیب فیلدها تغییر کرد' }),
}

// ─── §5.7 classification lookup table ────────────────────────────────────────

type ClassifierFn = (item: DiffItem) => Classification

const TABLE: Record<DiffItemType, Classification | ClassifierFn> = {
  // Field-level
  field_added: (i) => (i.details?.isRequired ? 'breaking' : 'safe'),
  field_removed: 'breaking',
  field_label_changed: 'safe',
  field_description_changed: 'safe',
  field_required_changed: (i) =>
    i.after === true ? 'breaking_pending_data_check' : 'safe',
  field_type_changed: 'breaking',
  field_default_changed: 'safe',

  // Options-level
  option_added: 'safe',
  option_removed: 'breaking_pending_data_check',
  option_label_changed: 'safe',
  option_order_changed: 'safe',

  // Validation-level
  validation_added: (i) =>
    (i.details?.severity as string | undefined) === 'blocking'
      ? 'breaking_pending_data_check'
      : 'cosmetic_runtime',
  validation_removed: 'safe',
  validation_tightened: 'breaking_pending_data_check',
  validation_loosened: 'safe',
  validation_severity_changed: (i) =>
    i.before === 'soft_warning' && i.after === 'blocking'
      ? 'breaking_pending_data_check'
      : 'safe',
  validation_message_changed: 'safe',

  // Conditional-level
  conditional_added: (i) =>
    (i.details?.hasRequireAction as boolean | undefined)
      ? 'breaking_pending_data_check'
      : 'safe',
  conditional_removed: 'cosmetic_runtime',
  conditional_action_changed: 'cosmetic_runtime',
  conditional_predicate_changed: 'cosmetic_runtime',

  // Calculated-level
  calculated_added: 'safe',
  calculated_removed: 'safe',
  calculated_formula_changed: 'cosmetic_runtime',

  // Form-level
  form_title_changed: 'safe',
  form_description_changed: 'safe',
  form_required_for_final_submit_changed: 'safe',
  form_minimum_instances_changed: (i) =>
    (i.after as number) > (i.before as number) ? 'breaking' : 'safe',
  form_instance_label_field_changed: 'safe',
  form_instance_type_changed: 'breaking',

  // Structure-level
  field_reordered: 'safe',
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function classify(item: DiffItem): Classification {
  const entry = TABLE[item.type]
  if (typeof entry === 'function') return entry(item)
  return entry
}

export function classifyAll(items: DiffItem[]): ClassifiedChange[] {
  return items.map((item) => {
    const classification = classify(item)
    const { label, detail } = LABELS[item.type]?.(item) ?? { label: item.type }
    return { item, classification, label, detail }
  })
}
